import type {
  OfflinePosPersistedState,
  OfflineReceiptHistoryEntry,
  OfflineReceiptStatus,
  OfflineSaleItem,
  OfflineSalePayload,
} from './types.js';
import { remainingQuantities } from '../utils/refund-quantities.js';

export function withReturnedQuantities(
  items: OfflineSaleItem[],
  refundItems: Array<{ productId: string; quantity: number }>,
): OfflineSaleItem[] {
  const prior = items
    .map((item) => ({
      productId: item.productId,
      quantity: item.returnedQuantity ?? 0,
    }))
    .filter((item) => item.quantity > 0);
  const left = remainingQuantities(items, [
    ...(prior.length > 0 ? [{ items: prior }] : []),
    { items: refundItems },
  ]);
  return items.map((item, index) => ({
    ...item,
    returnedQuantity: item.quantity - (left[index] ?? item.quantity),
  }));
}

export function salePayloadToHistoryEntry(
  payload: OfflineSalePayload,
  localReceiptId: string,
  cashierName: string,
  options?: { pendingSync?: boolean; status?: OfflineReceiptStatus },
): OfflineReceiptHistoryEntry {
  const now = new Date().toISOString();
  return {
    id: localReceiptId,
    saleVisibleId: localReceiptId,
    receiptNumber: localReceiptId,
    cartId: payload.cartId,
    branchId: payload.branchId,
    cashierName,
    items: payload.items,
    subtotal: payload.subtotal,
    discountPercentage: payload.discountPercentage,
    discountAmount: payload.discountAmount,
    grandTotal: payload.totalAmount,
    paymentMethod: payload.payment.method,
    cashGiven: payload.payment.cashGiven ?? payload.payment.amountPaid,
    cashReturned: payload.payment.cashReturned ?? 0,
    createdAt: now,
    status: options?.status ?? 'COMPLETED',
    pendingSync: options?.pendingSync ?? true,
  };
}

export function appendSalesHistoryEntry(
  state: OfflinePosPersistedState,
  entry: OfflineReceiptHistoryEntry,
): OfflinePosPersistedState {
  const withoutDup = state.salesHistory.filter(
    (e) => e.cartId !== entry.cartId && e.receiptNumber !== entry.receiptNumber,
  );
  return {
    ...state,
    salesHistory: [entry, ...withoutDup],
  };
}

export function mergePulledSalesHistory(
  state: OfflinePosPersistedState,
  branchId: string,
  pulled: OfflineReceiptHistoryEntry[],
): OfflinePosPersistedState {
  const localBranch = state.salesHistory.filter((e) => e.branchId === branchId);
  const otherBranches = state.salesHistory.filter((e) => e.branchId !== branchId);
  const localByCartId = new Map(localBranch.map((e) => [e.cartId, e]));
  const mergedByCartId = new Map<string, OfflineReceiptHistoryEntry>();

  for (const pulledEntry of pulled) {
    const local = localByCartId.get(pulledEntry.cartId);
    mergedByCartId.set(pulledEntry.cartId, reconcileHistoryEntry(local, pulledEntry));
  }

  for (const local of localBranch) {
    if (!mergedByCartId.has(local.cartId)) {
      mergedByCartId.set(local.cartId, local);
    }
  }

  const merged = [...mergedByCartId.values(), ...otherBranches];
  merged.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  return { ...state, salesHistory: merged };
}

/** Prefer local row when still pending sync or refund not yet reflected on BE pull. */
export function reconcileHistoryEntry(
  local: OfflineReceiptHistoryEntry | undefined,
  pulled: OfflineReceiptHistoryEntry,
): OfflineReceiptHistoryEntry {
  if (!local) return pulled;
  if (local.pendingSync) return local;
  if (
    (local.status === 'REFUNDED' || local.status === 'PARTIAL') &&
    pulled.status === 'COMPLETED'
  ) {
    return { ...pulled, status: local.status };
  }
  return pulled;
}

export function markReceiptRefundedInHistory(
  state: OfflinePosPersistedState,
  originalReceiptId: string,
  status: 'REFUNDED' | 'PARTIAL' = 'REFUNDED',
  refundItems?: Array<{ productId: string; quantity: number }>,
): OfflinePosPersistedState {
  const key = originalReceiptId.trim();
  return {
    ...state,
    salesHistory: state.salesHistory.map((e) => {
      if (e.receiptNumber !== key && e.id !== key && e.saleVisibleId !== key) return e;
      return {
        ...e,
        status,
        items:
          refundItems && refundItems.length > 0
            ? withReturnedQuantities(e.items, refundItems)
            : e.items,
      };
    }),
  };
}

export function ackSyncedSaleInHistory(
  state: OfflinePosPersistedState,
  cartId: string,
  server: { receiptId: string; saleVisibleId: string },
): OfflinePosPersistedState {
  return {
    ...state,
    salesHistory: state.salesHistory.map((e) =>
      e.cartId === cartId
        ? {
            ...e,
            id: server.saleVisibleId,
            saleVisibleId: server.saleVisibleId,
            receiptNumber: server.receiptId,
            pendingSync: false,
          }
        : e,
    ),
  };
}

export function getBranchSalesHistory(
  state: OfflinePosPersistedState,
  branchId: string,
): OfflineReceiptHistoryEntry[] {
  return state.salesHistory
    .filter((e) => e.branchId === branchId)
    .sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
}
